import { useEffect, useState } from 'react';
import { useHits } from 'react-instantsearch-hooks-web';
import { useRouter } from 'next/router';

import Building from '@/icons/duotone/building.svg';
import { CardFlat, Grid, NotFound } from '@/molecules';

import { cardTransformer } from '@/graphql/transformers';
import { capitalize, getQueryParams } from '@/utils';
import { gtmPush } from '@/utils/gtm';

import type { FunctionComponent } from 'react';
import type { TypeGridCardHitsProps } from './types';

/**
 * GridCardHits
 */
export const GridCardHits: FunctionComponent<TypeGridCardHitsProps> = ({
  className = '',
}: TypeGridCardHitsProps) => {
  const router = useRouter();
  const { hits, results } = useHits();
  const [currentPage, setCurrentPage] = useState<null | number>(null);
  const [isFilteringDates, setIsFilteringDates] = useState(false);
  const hierarchicalZonasKeys = Object.keys(
    results?._state.hierarchicalFacetsRefinements ?? {}
  );

  useEffect(() => {
    const { startDate, endDate } = router.query;
    setIsFilteringDates(startDate !== undefined || endDate !== undefined);

    if (
      typeof window !== 'undefined' &&
      hits &&
      results &&
      (results.page !== currentPage || isFilteringDates)
    ) {
      const listName =
        results?._state.hierarchicalFacetsRefinements[
          hierarchicalZonasKeys[0]
        ][0]?.toLowerCase();

      gtmPush({
        event: 'view_item_list',
        item_list_id: window.location.pathname,
        item_list_name: listName ?? 'Todos los pisos',
        page_number: results?.page,
        total_page_numbers: results?.nbPages,
        // TODO: tipar correctamente
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        search_term: getQueryParams(window.location.search)?.query,
        item_type: capitalize(hits?.[0]?.ventaAlquiler as string),
        items: hits?.map((hit: any) => ({
          item_id: hit?.codigoUnidadAlquiler,
          item_name: hit?.nombreUE,
          currency: 'EUR',
          start_date: startDate ? startDate : undefined,
          end_date: endDate ? endDate : undefined,
          discount: undefined, // TODO: añadir descuento cuando venga
          item_list_id: window.location.pathname,
          item_list_name: listName ?? 'Todos los pisos',
          price: parseFloat(
            hit.ventaAlquiler?.toLowerCase() === 'venta'
              ? hit.importeVenta
              : hit.importeRecibofinal
          ),
        })),
      });

      setIsFilteringDates(false);
      setCurrentPage(results?.page);
    }
  }, [results, hits]);

  if (hits.length == 0) {
    return (
      <NotFound
        className={`mx-auto ${className}`}
        text="No tenemos viviendas en esta ubicación"
        subtext="Cambia o elimina algunos de los filtros o modifica el área de búsqueda."
        Icon={Building}
      />
    );
  }

  return (
    <Grid xxs={1} sm={2} lg={1} xl={2} className={`gap-6 ${className}`}>
      {hits.map((hit: any, key: number) => (
        <Grid.Item colSpanSM={1} key={hit?.id ?? key}>
          <CardFlat
            {...cardTransformer(
              hit,
              // eslint-disable-next-line @typescript-eslint/no-empty-function
              () => {}
            )}
          />
        </Grid.Item>
      ))}
    </Grid>
  );
};

GridCardHits.displayName = 'GridCardHits';
